import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { apiRequest, apiRequestWithStatus } from "../../common/api/apiClient";
import { uploadImage } from "../../tierApi/supabase";
import type { Spot, SpotApiResponse } from "../../types/entities";

// --- Types ---

interface SpotsState {
  spots: Record<number, Spot>;
  isLoadingSpots: boolean;
  failedToLoadSpots: boolean;
  isLoadingSpotCreation: boolean;
  failedToCreateSpot: boolean;
  isLoadingLikeSpot: boolean;
  failedToLikeSpot: boolean;
  displayedSpotsCount: number;
  spotsPerPage: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  totalCount: number;
  currentFilters: Record<string, unknown>;
}

interface CreateSpotPayload {
  name: string;
  country: string;
  wifiQuality: string;
  selectedFile: File | null;
}

interface SpotFilters {
  lifeCost?: string;
  hasColiving?: boolean;
  hasCoworking?: boolean;
  wifiQuality?: string;
  country?: string;
  surfSeason?: string[];
}

// --- Helpers ---

const createSpotObject = (spot: SpotApiResponse): Spot => {
  return {
    id: spot.id,
    name: spot.name,
    country: spot.country,
    image_link: spot.image_link,
    surfSeason: spot.surf_season?.split(",") ?? [],
    wifiQuality: spot.wifi_quality,
    hasCoworking: spot.has_coworking,
    hasColiving: spot.has_coliving,
    lifeCost: spot.life_cost,
    submittedBy: spot.submitted_by,
    latitude: parseFloat(spot.latitude),
    longitude: parseFloat(spot.longitude),
    likeUserIds: spot.like_user_ids || [],
    totalLikes: parseInt(spot.total_likes) || 0,
    sortKey: parseInt(spot.total_likes) || 0,
    creatorName: spot.creator_name,
    summary: spot.summary,
  };
};

// --- Async Thunks ---

export const createSpot = createAsyncThunk(
  "spots/createSpot",
  async (
    spotData: CreateSpotPayload,
    { getState }: { getState: () => any },
  ) => {
    const { name, country, wifiQuality, selectedFile } = spotData;

    let image_link: string | null = null;

    if (selectedFile) {
      try {
        image_link = await uploadImage(selectedFile, "spot", name, country);
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    }

    const data = {
      name,
      country,
      image_link,
      wifi_quality: parseInt(wifiQuality),
    };

    const token = getState().user.session?.access_token;
    const responseData = await apiRequest<SpotApiResponse>("/spots", {
      method: "POST",
      body: data,
      token,
    });

    return createSpotObject(responseData);
  },
);

export const loadSpots = createAsyncThunk(
  "spots/loadSpots",
  async ({ filters = {} }: { filters?: SpotFilters } = {}) => {
    const {
      lifeCost,
      hasColiving,
      hasCoworking,
      wifiQuality,
      country,
      surfSeason = [],
    } = filters || {};

    const queryParams = new URLSearchParams();

    if (lifeCost) queryParams.append("lifeCost", lifeCost);
    if (hasColiving) queryParams.append("hasColiving", String(hasColiving));
    if (hasCoworking) queryParams.append("hasCoworking", String(hasCoworking));
    if (wifiQuality) queryParams.append("wifiQuality", wifiQuality);
    if (country) queryParams.append("country", country);
    if (surfSeason && surfSeason.length) {
      surfSeason.forEach((season) => queryParams.append("surfSeason", season));
    }

    const queryString = queryParams.toString();
    const path = `/spots${queryString ? `?${queryString}` : ""}`;

    const json = await apiRequest<any>(path);

    const spots = json.spots || json;
    const totalCount: number = json.totalCount || spots.length;

    const cardsData = spots.reduce(
      (acc: Record<number, Spot>, spot: SpotApiResponse) => {
        acc[spot.id] = createSpotObject(spot);
        return acc;
      },
      {},
    );

    return {
      spots: cardsData,
      totalCount,
    };
  },
);

export const loadMoreSpots = createAsyncThunk(
  "spots/loadMoreSpots",
  async (_: void, { getState }: { getState: () => any }) => {
    const state = getState();
    const { displayedSpotsCount, spotsPerPage, spots } = state.spots;

    const newDisplayedCount = displayedSpotsCount + spotsPerPage;
    const totalSpots = Object.keys(spots).length;
    const hasMore = newDisplayedCount < totalSpots;

    return {
      displayedSpotsCount: newDisplayedCount,
      hasMore,
    };
  },
);

export const likeSpot = createAsyncThunk(
  "spots/likeSpot",
  async (spotId: number, { getState }: { getState: () => any }) => {
    const token = getState().user.session?.access_token;
    const { data, status } = await apiRequestWithStatus<{ userId: string }>(
      `/spots/${spotId}/like`,
      { method: "POST", token },
    );

    return { json: data, status, spotId, userId: data.userId };
  },
);

// --- Slice ---

const initialState: SpotsState = {
  spots: {},
  isLoadingSpots: false,
  failedToLoadSpots: false,
  isLoadingSpotCreation: false,
  failedToCreateSpot: false,
  isLoadingLikeSpot: false,
  failedToLikeSpot: false,
  displayedSpotsCount: 15,
  spotsPerPage: 15,
  hasMore: false,
  isLoadingMore: false,
  totalCount: 0,
  currentFilters: {},
};

export const spotsSlice = createSlice({
  name: "spots",
  initialState,
  reducers: {
    resetPagination: (state) => {
      state.displayedSpotsCount = 15;
      state.hasMore = false;
      state.spots = {};
      state.totalCount = 0;
    },
    setCurrentFilters: (
      state,
      action: PayloadAction<Record<string, unknown>>,
    ) => {
      state.currentFilters = action.payload;
    },
    resetDisplayedCount: (state) => {
      state.displayedSpotsCount = 15;
      const totalSpots = Object.keys(state.spots).length;
      state.hasMore = state.displayedSpotsCount < totalSpots;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSpots.pending, (state) => {
        state.isLoadingSpots = true;
        state.failedToLoadSpots = false;
      })
      .addCase(loadSpots.rejected, (state) => {
        state.isLoadingSpots = false;
        state.failedToLoadSpots = true;
      })
      .addCase(loadSpots.fulfilled, (state, action) => {
        state.isLoadingSpots = false;
        state.failedToLoadSpots = false;

        const { spots, totalCount } = action.payload;

        state.spots = spots;
        state.totalCount = totalCount;

        state.displayedSpotsCount = Math.min(state.spotsPerPage, totalCount);
        state.hasMore = state.displayedSpotsCount < totalCount;
      })
      .addCase(loadMoreSpots.pending, (state) => {
        state.isLoadingMore = true;
      })
      .addCase(loadMoreSpots.rejected, (state) => {
        state.isLoadingMore = false;
      })
      .addCase(loadMoreSpots.fulfilled, (state, action) => {
        state.isLoadingMore = false;

        const { displayedSpotsCount, hasMore } = action.payload;

        state.displayedSpotsCount = displayedSpotsCount;
        state.hasMore = hasMore;
      })
      .addCase(createSpot.pending, (state) => {
        state.isLoadingSpotCreation = true;
        state.failedToCreateSpot = false;
      })
      .addCase(createSpot.rejected, (state) => {
        state.isLoadingSpotCreation = false;
        state.failedToCreateSpot = true;
      })
      .addCase(createSpot.fulfilled, (state, action) => {
        state.isLoadingSpotCreation = false;
        state.failedToCreateSpot = false;
        state.spots[action.payload.id] = action.payload;
        state.totalCount += 1;
        const totalSpots = Object.keys(state.spots).length;
        state.hasMore = state.displayedSpotsCount < totalSpots;
      })
      .addCase(likeSpot.pending, (state) => {
        state.isLoadingLikeSpot = true;
        state.failedToLikeSpot = false;
      })
      .addCase(likeSpot.rejected, (state) => {
        state.isLoadingLikeSpot = false;
        state.failedToLikeSpot = true;
      })
      .addCase(likeSpot.fulfilled, (state, action) => {
        state.isLoadingLikeSpot = false;
        state.failedToLikeSpot = false;
        const { spotId, status, userId } = action.payload;
        const spot = state.spots[spotId];
        if (spot) {
          if (status === 201) {
            spot.likeUserIds.push(userId);
            spot.totalLikes += 1;
          } else if (status === 200) {
            spot.likeUserIds = spot.likeUserIds.filter((id) => id !== userId);
            spot.totalLikes -= 1;
          }
        }
      });
  },
});

export const { resetPagination, setCurrentFilters, resetDisplayedCount } =
  spotsSlice.actions;

// Selectors — use `any` for state until RootState is available from store.ts

export const selectSpots = createSelector(
  [(state: any) => state.spots.spots],
  (spots: Record<number, Spot>) => {
    return Object.entries(spots)
      .map(([, spot]) => spot)
      .sort((a, b) => b.sortKey - a.sortKey);
  },
);
export const selectSpot = (state: any, spotId: number | string) =>
  state.spots?.spots?.[spotId] as Spot | undefined;
export const selectDisplayedSpotsCount = (state: any) =>
  state.spots.displayedSpotsCount as number;
export const selectHasMore = (state: any) => state.spots.hasMore as boolean;
export const selectIsLoadingMore = (state: any) =>
  state.spots.isLoadingMore as boolean;
export const selectTotalCount = (state: any) =>
  state.spots.totalCount as number;
export const failedToLoadSpots = (state: any) =>
  state.spots.failedToLoadSpots as boolean;
export const isLoadingSpots = (state: any) =>
  state.spots.isLoadingSpots as boolean;

export default spotsSlice.reducer;
